/**
 * Machine / installation binding at startup.
 * Manager/owner sessions: always may proceed.
 * Active cashier sessions on device-locked businesses: POS machine enforced.
 */

const MACHINE_BINDING_TIMEOUT_MS = 5000;

function withTimeout(promise, ms, label) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`[Startup] ${label} timed out after ${ms}ms`)),
      ms
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * @returns {Promise<{ canProceed: boolean, reason?: string, blocked?: boolean }>}
 */
export async function runStartupMachineBinding({ storedUserEarly = null } = {}) {
  const earlyUser =
    storedUserEarly && typeof storedUserEarly === 'object' ? storedUserEarly : null;
  if (earlyUser?.type === 'admin') {
    return { canProceed: true, reason: 'Admin-Login' };
  }

  const { machineBindingService } = await import('./machineBindingService');
  const { offlineAuthService } = await import('./offlineAuthService');
  const { allowMachineRebindForBusiness } = await import('../utils/machineBindingPolicy');
  const { loadRoleForBusiness } = await import('../utils/roleStorage');

  const localList = offlineAuthService.getBusinesses() || [];
  const businessId =
    earlyUser?.business?.id || earlyUser?.business?.cloudId || earlyUser?.user?.id || null;
  let activeRole = null;
  if (businessId) {
    try {
      activeRole = loadRoleForBusiness(businessId);
    } catch {
      /* optional */
    }
  }
  // Active cashier: always treat install/machine mismatch as hard block
  const cashierLocked = activeRole?.type === 'worker';

  // Install rebind only for pure soft-offline reinstall (not when a cashier session is active)
  const allAllowSoftRebind =
    localList.length > 0 && localList.every((b) => allowMachineRebindForBusiness(b));

  try {
    const installCheck = await withTimeout(
      machineBindingService.ensureInstallationBinding({
        allowRebind: !cashierLocked && allAllowSoftRebind && !storedUserEarly
      }),
      MACHINE_BINDING_TIMEOUT_MS,
      'installation binding'
    );
    if (!installCheck.canProceed && cashierLocked) {
      return {
        canProceed: false,
        blocked: true,
        reason: installCheck.reason || 'Kassierer nur auf dem POS-Rechner.'
      };
    }
    // Manager: ignore install block
  } catch (bindErr) {
    console.warn('[Startup] Installation binding skipped:', bindErr?.message || bindErr);
    if (cashierLocked) {
      return {
        canProceed: false,
        blocked: true,
        reason: 'Geräteprüfung abgelaufen — Kassierer-Session gesperrt.'
      };
    }
  }

  try {
    const machineCheck = await withTimeout(
      machineBindingService.checkOnStartup(),
      MACHINE_BINDING_TIMEOUT_MS,
      'machine binding check'
    );
    if (!machineCheck.canProceed) {
      return {
        canProceed: false,
        blocked: true,
        reason: machineCheck.reason || 'Kassierer nur auf dem installierten POS-Rechner.'
      };
    }
    return { canProceed: true };
  } catch (machineErr) {
    console.warn('[Startup] Machine check skipped:', machineErr?.message || machineErr);
    if (cashierLocked) {
      return {
        canProceed: false,
        blocked: true,
        reason: 'Geräteprüfung fehlgeschlagen — Kassierer-Session gesperrt.'
      };
    }
    return { canProceed: true };
  }
}
